import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { withStore } from '@bigyellow/store'
import PasswordShowHide from '@bigyellow/components/Forms/PasswordShowHide'
import PasswordComplexity from '../PasswordComplexity'

/**
 * CreatePassword component allows users to create a new password.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.description='Thanks. Please now create a password, which you will need when you next log in.'] - Description text displayed above the form.
 * @param {string} [props.buttonLabel='Save details'] - Label for the submit button.
 * @param {function} props.OnSuccess - Callback function to be called when the password is successfully created.
 * @param {string} props.OnSuccess.password - The new password created by the user.
 * @returns {JSX.Element} The rendered component.
 */
const CreatePassword = ({
  description = 'Thanks. Please now create a password, which you will need when you next log in.',
  buttonLabel = 'Save details',
  disabled,
  OnSuccess
}) => {
  const [isSubmitting, setSubmitting] = useState(false)

  /**
   * Handles the form submission.
   *
   * @param {Object} values - The form values.
   * @param {string} values.password - The new password.
   */
  const createPassword = ({ password }) => {
    setSubmitting(true)
    console.log('OnSuccess arguments:', password)
    OnSuccess(password)
  }

  const schema = Yup.object().shape({
    password: Yup.string().required('This field is required').test('password', 'Password is invalid', (value) => {
      const check_a = value?.replace(/[^A-Z]/g, '').length >= 1
      const check_b = value?.length >= 8
      const check_c = value?.replace(/[^0-9]/g, '').length

      if (value) {
        return check_a && check_b && check_c
      }
    }).nullable(),
    confirm_password: Yup.string().min(6)
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('This field is required')
  })

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={createPassword}
      initialValues={{ password: '' }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        isValid,
        errors
      }) => (
        <Form noValidate onSubmit={handleSubmit} id="onlineAccessForm">
          <React.Fragment>
            {
              description ? (
                    <Row className={'mb-4'}>
                      <Col>
                        <p>{description}</p>
                      </Col>
                    </Row>
              ) : null
            }

            <Form.Row>
              <Form.Group as={Col} controlId="validationPassword" className="material material label-top">
                <PasswordShowHide
                  name="password"
                  value={values.password}
                  label={'Password'}
                  onChange={handleChange}
                  disabled={!isSubmitting}
                  isInvalid={touched.password && errors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
                <PasswordComplexity values={values} />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId="validationConfirmPassword" className="material material label-top">
                <PasswordShowHide
                  name="confirm_password"
                  label={`Confirm password`}
                  value={values.confirm_password}
                  onChange={handleChange}
                  disabled={!isSubmitting}
                  isInvalid={touched.confirm_password && errors.confirm_password}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirm_password}
                </Form.Control.Feedback>
              </Form.Group>
            </Form.Row>
            <Row className={'my-5'}>
              <Col className={`text-center`}>
                <Button type="submit" className="" disabled={isSubmitting || disabled}>
                  {buttonLabel}
                </Button>
              </Col>
            </Row>
          </React.Fragment>
        </Form>
      )}
    </Formik>
  )
}

CreatePassword.propTypes = {
  description: PropTypes.string,
  buttonLabel: PropTypes.string,
  OnSuccess: PropTypes.func.isRequired
}

export default withStore(CreatePassword)